import axios from 'axios';
import { checkRefreshToken } from './refreshToken';

// ======================== Configuration ========================
const BASE_URL = 'https://loyalty.bom.asia';

// Axios instance với cấu hình mặc định
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ======================== Interceptors ========================

// Request interceptor - Tự động thêm token vào headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Tự động refresh token khi hết hạn
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Nếu lỗi 401 (Unauthorized - token hết hạn)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Tránh việc login API bị retry (vì login fail cũng trả 401)
      if (originalRequest.url?.includes('/adminLogin')) {
        console.error('❌ Login failed:', error);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Nếu đang refresh, đưa request vào queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const accessToken = localStorage.getItem('authToken');
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          console.error('❌ No refresh token found - logging out');
          handleLogout();
          return Promise.reject(error);
        }

        const result = await checkRefreshToken(accessToken, refreshToken);

        if (result.accessToken) {
          localStorage.setItem('authToken', result.accessToken);
          
          if (result.refreshToken) {
            localStorage.setItem('refresh_token', result.refreshToken);
          }

          // Update token cho request ban đầu
          originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
          
          // Process queue
          processQueue(null, result.accessToken);
          
          // Retry request ban đầu
          return apiClient(originalRequest);
        } else {
          console.error('❌ Refresh token failed - logging out');
          handleLogout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('❌ Error refreshing token:', refreshError);
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Các lỗi khác
    console.error('API Error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

// Helper function để logout và redirect
const handleLogout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('adminId');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('login');
  localStorage.removeItem('authenticated');
  localStorage.removeItem('profileURL');
  localStorage.removeItem('Name');
  sessionStorage.clear();
  
  
  // Redirect về trang login
  if (typeof window !== 'undefined') {
    window.location.href = `${process.env.PUBLIC_URL || ''}/login`;
  }
};

// ======================== API Functions ========================

/**
 * Đăng nhập
 * @param {string} username - Tên đăng nhập
 * @param {string} password - Mật khẩu
 * @returns {Promise<Object>} Response data với token và admin info
 */
export const login = async (username, password) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/admin/adminLogin`,
      { username, password },
      { headers: { 'Content-Type': 'application/json' } }
    );
    
    // Lưu token vào localStorage (hỗ trợ cả token và accessToken)
    const token = response.data.token || response.data.accessToken;
    if (token) {
      localStorage.setItem('authToken', token);
    }
    
    // Lưu refresh token nếu có
    if (response.data.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }
    
    // Lưu admin ID
    if (response.data.admin?.id) {
      localStorage.setItem('adminId', response.data.admin.id.toString());
    }
    
    return response.data;
  } catch (error) {
    console.error('Error login:', error);
    throw error.response?.data || { message: 'Login failed' };
  }
};

/**
 * Đăng ký Admin mới
 * @param {string} name - Tên admin
 * @param {string} username - Tên đăng nhập
 * @param {string} password - Mật khẩu
 * @param {string} role_code - Mã vai trò
 * @returns {Promise<Object>} Thông tin admin mới
 */
export const register = async (name, username, password, role_code) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Token không tồn tại');
    }

    const response = await apiClient.post('/api/admin/adminRegister', {
      name,
      username,
      password,
      role_code,
    });
    
    return response.data;
  } catch (error) {
    console.error('Error register:', error);
    throw error.response?.data || { message: 'Register failed' };
  }
};

/**
 * Lấy danh sách tất cả Admin
 * @returns {Promise<Array>} Danh sách admin
 */
export const loadingAllAdmin = async () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Token không tồn tại');
    }

    const response = await apiClient.get('/api/admin/allAdmin');
    
    return response.data.admins || [];
  } catch (error) {
    console.error('Error fetching admins:', error);
    throw error.response?.data || { message: 'Failed to fetch admins' };
  }
};

/**
 * Lấy thông tin Admin theo ID
 * @param {string} id - ID của admin
 * @returns {Promise<Object>} Thông tin admin
 */
export const getAdminById = async (id) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Token không tồn tại');
    }

    const response = await apiClient.get(`/api/admin/admin/${id}`);
    
    return response.data.admin;
  } catch (error) {
    console.error('Error getting admin by ID:', error);
    throw error.response?.data || { message: 'Failed to get admin by ID' };
  }
};

/**
 * Xóa Admin
 * @param {string} id - ID của admin cần xóa
 * @returns {Promise<Object>} Kết quả xóa
 */
export const deleteAdmin = async (id) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Token không tồn tại');
    }

    const response = await apiClient.delete(`/api/admin/delete/${id}`);
    
    return { id, ...response.data };
  } catch (error) {
    console.error('Error deleting admin:', error);
    throw error.response?.data || { message: 'Failed to delete admin' };
  }
};

/**
 * Sửa thông tin Admin
 * @param {string} id - ID của admin cần sửa
 * @param {Object} data - Dữ liệu cần cập nhật
 * @returns {Promise<Object>} Thông tin admin đã cập nhật
 */
export const editAdmin = async (id, data) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Token không tồn tại');
    }

    const response = await apiClient.put(`/api/admin/update/${id}`, data);
    
    return response.data;
  } catch (error) {
    console.error('Error editing admin:', error);
    throw error.response?.data || { message: 'Failed to edit admin' };
  }
};

/**
 * Đăng xuất
 * @returns {Promise<Object>} Thông báo đăng xuất
 */
export const logout = async () => {
  try {
    // Xóa tất cả auth tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminId');
    localStorage.removeItem('refresh_token');
    
    // Xóa thông tin login
    localStorage.removeItem('login');
    localStorage.removeItem('authenticated');
    
    // Xóa thông tin profile
    localStorage.removeItem('profileURL');
    localStorage.removeItem('Name');
    
    // Xóa cookies nếu có (document.cookie)
    // Clear all cookies
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name.trim() + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }
    
    // Xóa session storage nếu có
    sessionStorage.clear();
    
    return { message: 'Đăng xuất thành công' };
  } catch (error) {
    console.error('❌ Error logout:', error);
    throw { message: 'Đăng xuất thất bại' };
  }
};

// ======================== Utility Functions ========================

/**
 * Kiểm tra xem user đã đăng nhập chưa
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  return !!token;
};

/**
 * Lấy token hiện tại
 * @returns {string|null}
 */
export const getToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Lấy Admin ID hiện tại
 * @returns {string|null}
 */
export const getAdminId = () => {
  return localStorage.getItem('adminId');
};

// ======================== Default Export ========================
export default {
  login,
  register,
  loadingAllAdmin,
  getAdminById,
  deleteAdmin,
  editAdmin,
  logout,
  isAuthenticated,
  getToken,
  getAdminId,
};


